import React, { useMemo, useState } from 'react';

const CooccurrenceHeatmap = ({ mutations, topGenes = 20 }) => {
  const [selectedCell, setSelectedCell] = useState(null);

  // Calculate gene frequencies
  const geneFrequencies = useMemo(() => {
    const freqs = {};
    mutations.forEach(m => {
      freqs[m.gene] = (freqs[m.gene] || 0) + 1;
    });
    return Object.entries(freqs)
      .sort((a, b) => b[1] - a[1])
      .slice(0, topGenes)
      .map(([gene]) => gene);
  }, [mutations, topGenes]);

  // Calculate co-occurrence matrix
  const cooccurrenceMatrix = useMemo(() => {
    const matrix = {};
    const cancerTypes = {};

    // Group mutations by cancer type
    mutations.forEach(m => {
      m.cancerTypes.forEach(ct => {
        if (!cancerTypes[ct]) cancerTypes[ct] = new Set();
        cancerTypes[ct].add(m.gene);
      });
    });

    // Calculate co-occurrence
    geneFrequencies.forEach(gene1 => {
      matrix[gene1] = {};
      geneFrequencies.forEach(gene2 => {
        if (gene1 === gene2) {
          matrix[gene1][gene2] = { cooccur: 0, exclusive: 0, score: 0, type: 'self' };
        } else {
          let cooccur = 0;
          let gene1Only = 0;
          let gene2Only = 0;

          Object.values(cancerTypes).forEach(genes => {
            const hasGene1 = genes.has(gene1);
            const hasGene2 = genes.has(gene2);
            if (hasGene1 && hasGene2) cooccur++;
            if (hasGene1 && !hasGene2) gene1Only++;
            if (!hasGene1 && hasGene2) gene2Only++;
          });

          // Calculate log odds ratio for co-occurrence
          const total = Object.keys(cancerTypes).length;
          const neither = total - cooccur - gene1Only - gene2Only;

          let score = 0;
          let type = 'neutral';

          if (cooccur > 0 && neither > 0) {
            const oddsRatio = (cooccur * neither) / Math.max((gene1Only * gene2Only), 0.5);
            score = Math.log2(oddsRatio);
            type = score > 0.5 ? 'co-occurring' : score < -0.5 ? 'mutually_exclusive' : 'neutral';
          }

          matrix[gene1][gene2] = { cooccur, gene1Only, gene2Only, score, type };
        }
      });
    });

    return matrix;
  }, [mutations, geneFrequencies]);

  // Color scale for heatmap
  const getColor = (score, type) => {
    if (type === 'self') return '#f0f0f0';
    if (type === 'co-occurring') {
      const intensity = Math.min(Math.abs(score) / 3, 1);
      return `rgba(46, 204, 113, ${0.3 + intensity * 0.7})`;
    }
    if (type === 'mutually_exclusive') {
      const intensity = Math.min(Math.abs(score) / 3, 1);
      return `rgba(231, 76, 60, ${0.3 + intensity * 0.7})`;
    }
    return '#f8f9fa';
  };

  const cellSize = 35;
  const labelWidth = 80;
  const margin = { top: 100, left: labelWidth + 10 };
  const width = margin.left + geneFrequencies.length * cellSize + 100;
  const height = margin.top + geneFrequencies.length * cellSize + 20;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-2">Gene Co-occurrence / Mutual Exclusivity</h3>
      <p className="text-sm text-gray-600 mb-4">
        Green = co-occurring | Red = mutually exclusive | Based on shared cancer types
      </p>

      <div className="overflow-x-auto">
        <svg width={width} height={height}>
          {/* Column labels (top) */}
          {geneFrequencies.map((gene, i) => (
            <text
              key={`col-${gene}`}
              x={margin.left + i * cellSize + cellSize / 2}
              y={margin.top - 10}
              textAnchor="start"
              transform={`rotate(-45, ${margin.left + i * cellSize + cellSize / 2}, ${margin.top - 10})`}
              fontSize="10"
              fill="#2c3e50"
            >
              {gene}
            </text>
          ))}

          {/* Row labels (left) */}
          {geneFrequencies.map((gene, i) => (
            <text
              key={`row-${gene}`}
              x={margin.left - 5}
              y={margin.top + i * cellSize + cellSize / 2 + 4}
              textAnchor="end"
              fontSize="10"
              fill="#2c3e50"
            >
              {gene}
            </text>
          ))}

          {/* Heatmap cells */}
          {geneFrequencies.map((gene1, i) => (
            geneFrequencies.map((gene2, j) => {
              const data = cooccurrenceMatrix[gene1]?.[gene2];
              if (!data) return null;

              return (
                <g key={`${gene1}-${gene2}`}>
                  <rect
                    x={margin.left + j * cellSize}
                    y={margin.top + i * cellSize}
                    width={cellSize - 2}
                    height={cellSize - 2}
                    fill={getColor(data.score, data.type)}
                    stroke={selectedCell === `${gene1}-${gene2}` ? '#2c3e50' : '#e0e0e0'}
                    strokeWidth={selectedCell === `${gene1}-${gene2}` ? 2 : 0.5}
                    className="cursor-pointer"
                    onMouseEnter={() => setSelectedCell(`${gene1}-${gene2}`)}
                    onMouseLeave={() => setSelectedCell(null)}
                  />
                  {/* Show count in cell if co-occurring */}
                  {data.cooccur > 0 && data.type !== 'self' && (
                    <text
                      x={margin.left + j * cellSize + cellSize / 2 - 1}
                      y={margin.top + i * cellSize + cellSize / 2 + 3}
                      textAnchor="middle"
                      fontSize="9"
                      fill={Math.abs(data.score) > 1 ? 'white' : '#2c3e50'}
                    >
                      {data.cooccur}
                    </text>
                  )}
                </g>
              );
            })
          ))}

          {/* Legend */}
          <g transform={`translate(${margin.left + geneFrequencies.length * cellSize + 15}, ${margin.top})`}>
            <text x={0} y={0} fontSize="11" fontWeight="bold" fill="#2c3e50">Legend</text>

            <rect x={0} y={15} width={20} height={15} fill="rgba(46, 204, 113, 0.8)" />
            <text x={25} y={27} fontSize="10" fill="#2c3e50">Co-occurring</text>

            <rect x={0} y={35} width={20} height={15} fill="rgba(231, 76, 60, 0.8)" />
            <text x={25} y={47} fontSize="10" fill="#2c3e50">Exclusive</text>

            <rect x={0} y={55} width={20} height={15} fill="#f8f9fa" stroke="#e0e0e0" />
            <text x={25} y={67} fontSize="10" fill="#2c3e50">Neutral</text>
          </g>
        </svg>
      </div>

      {/* Tooltip */}
      {selectedCell && (
        <div className="mt-4 p-3 bg-gray-100 rounded text-sm">
          {(() => {
            const [gene1, gene2] = selectedCell.split('-');
            const data = cooccurrenceMatrix[gene1]?.[gene2];
            if (!data || data.type === 'self') return null;
            return (
              <div>
                <strong>{gene1} & {gene2}</strong>
                <div className="mt-1 text-gray-600">
                  Shared cancer types: {data.cooccur} |
                  {gene1} only: {data.gene1Only} |
                  {gene2} only: {data.gene2Only}
                </div>
                <div className={`mt-1 font-semibold ${
                  data.type === 'co-occurring' ? 'text-green-600' :
                  data.type === 'mutually_exclusive' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {data.type === 'co-occurring' ? 'Co-occurring' :
                   data.type === 'mutually_exclusive' ? 'Mutually Exclusive' : 'Neutral'}
                  {data.score !== 0 && ` (score: ${data.score.toFixed(2)})`}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CooccurrenceHeatmap;
